import { FC, useRef } from "react";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
} from "../../../../components";
import MuiChip from "../../../../components/chip/MuiChip";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../components/statusBadge/StatusBadge";
import { UnitTypesKey } from "../../../../helpers/constants/siteCoreKeys";
import {
  isMeasurementMinutes,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { addTopicSiteCoreKeys } from "../../../mdmAddTopic/siteCore/SiteCoreKeys";
import { TopicDetailsDataType } from "../../modelsAndSchemas/models";
import { MuiDoubleLabelWithChip } from "../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";

interface TopicDetailsProps {
  pageId: string;
  topicDetailsData: TopicDetailsDataType;
  selectedUnit: string;
}

export const TopicDetails: FC<TopicDetailsProps> = ({
  pageId,
  topicDetailsData,
  selectedUnit,
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const { fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const {
    addTopic,
    topicNameLabel,
    deliverablesLabel,
    minutesList,
    minutesLabel,
    quantityLabel,
    numberOfDaysLabel,
    halfDayLabel,
    activityStatusList,
    activityStatusLabel,
    participantStatusLabel,
    selectedPreferences,
    selectedDeliverables,
  } = addTopicSiteCoreKeys;

  const getViewTopicLabel = (key: string) => {
    return fetchLabel(addTopic, key);
  };

  const activityStatus = fetchValueByKeyOfDropdown(
    addTopic,
    activityStatusList,
    topicDetailsData?.activityStatus ?? "",
  );

  const getUnitHeaderKey = () => {
    switch (selectedUnit) {
      case UnitTypesKey.Minutes:
        return minutesLabel;
      case UnitTypesKey.Count:
        return quantityLabel;
      case UnitTypesKey.Days:
        return numberOfDaysLabel;
      default:
        return halfDayLabel;
    }
  };

  const getMeasurementValue = () => {
    if (!selectedUnit) {
      return "";
    }
    if (isMeasurementMinutes(selectedUnit)) {
      return fetchValueByKeyOfDropdown(
        addTopic,
        minutesList,
        topicDetailsData?.measurement,
      );
    }

    return topicDetailsData?.measurement;
  };

  const renderChip = (key: string) => {
    return (
      <MuiChip
        data={[
          {
            value: key,
            label: getViewTopicLabel(key),
          },
        ]}
        wrapperClass="preferences-chip"
        isEditable={false}
        showMoreARIALabel={commonARIALabel.showNMore}
        removeChipNameARIALabel={commonARIALabel.removeChipName}
      />
    );
  };

  const preferenceConfig = [
    {
      condition: topicDetailsData?.triggerBilling,
      siteCoreKey: addTopicSiteCoreKeys.triggerBilling,
    },
    {
      condition: topicDetailsData?.countsTowardsLastContactDate,
      siteCoreKey: addTopicSiteCoreKeys.countTowardsLastContactDate,
    },
    {
      condition: topicDetailsData?.triggerTouchPointSurvey,
      siteCoreKey: addTopicSiteCoreKeys.triggerTouchPointSurvey,
    },
  ];

  const activePreferences = preferenceConfig.filter((p) => p.condition);

  return (
    <MuiBox
      className="topic-details-container"
      data-testid={`${pageId}-topic-container`}
    >
      <MuiGrid container>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getViewTopicLabel(topicNameLabel)}
            value={topicDetailsData?.topicName}
            isValueWithEllipsis
            replaceEmptyWithDash
          />
        </MuiGrid>

        <MuiGrid size={gridSize} ref={gridRef} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getViewTopicLabel(deliverablesLabel)}
            replaceEmptyWithDash={!topicDetailsData?.deliverables.length}
            value={""}
          />
          {topicDetailsData?.deliverables.length > 0 && (
            <MuiChipWithPopover
              data={setApiDataToDropDownOption(topicDetailsData?.deliverables)}
              id={"selected-deliverables"}
              headerLabel={getViewTopicLabel(selectedDeliverables)}
              gridRef={gridRef}
              isEditable={false}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
          )}
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getViewTopicLabel(getUnitHeaderKey())}
            value={getMeasurementValue()}
          />
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getViewTopicLabel(activityStatusLabel)}
            value={
              <StatusBadge
                status={topicDetailsData?.activityStatus as StatusBadgeType}
                label={activityStatus}
              />
            }
          />
        </MuiGrid>

        <MuiGrid size={gridSize} ref={gridRef} className={gridSizeMediumClass}>
          <MuiDoubleLabelWithChip
            label={getViewTopicLabel(participantStatusLabel)}
            replaceEmptyWithDash={!topicDetailsData?.participantStatus.length}
            value={topicDetailsData?.participantStatus?.map((item: string) => ({
              label: fetchValueByKeyOfDropdown(
                addTopicSiteCoreKeys.addTopic,
                addTopicSiteCoreKeys.participantStatusList,
                item,
              ),
              value: item,
            }))}
            chipPopoverHeaderLabel={getViewTopicLabel(participantStatusLabel)}
            chipPopoverGridRef={gridRef}
             showMoreARIALabel={commonARIALabel.showNMore}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
          />
        </MuiGrid>
      </MuiGrid>

      {activePreferences?.length > 0 && (
        <MuiBox className="selected-preferences">
          <MuiTypography variant="h3Bold" component="h3">
            {getViewTopicLabel(selectedPreferences)}
          </MuiTypography>
          <MuiBox className="preferences-container">
            {activePreferences.map(({ siteCoreKey }) =>
              renderChip(siteCoreKey),
            )}
          </MuiBox>
        </MuiBox>
      )}
    </MuiBox>
  );
};
